import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { getProductType, PRODUCT_TYPES, ADULT_BASE_PRICE, KIDS_BASE_PRICE } from "../data/catalog";
import { getAllThemes } from "../lib/dynamicCatalog";
import { useDefaultPrices, resolveDefaultPrice } from "../lib/defaultPrices";
import { BoltIcon } from "./Bolts";
import { ImageOff, ArrowRight, ArrowLeft } from "lucide-react";
import { fmtPrice } from "../contexts/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProductListings = ({ category, subcategory, headerTitle, headerSub }) => {
    const [listings, setListings] = useState([]);
    const [loading, setLoading] = useState(true);
    // null = show the theme picker; "all" or a theme slug = show those products
    const [activeTheme, setActiveTheme] = useState(null);
    const priceMap = useDefaultPrices();

    const productType = useMemo(() => getProductType(category, subcategory), [category, subcategory]);
    const defaultPrice = useMemo(
        () => resolveDefaultPrice(priceMap, category, subcategory),
        [priceMap, category, subcategory]
    );

    useEffect(() => {
        let cancelled = false;
        setActiveTheme(null); // back to the picker whenever the subcategory changes
        (async () => {
            setLoading(true);
            try {
                const params = { category };
                if (subcategory) params.subcategory = subcategory;
                const { data } = await axios.get(`${API}/listings/public`, { params });
                if (!cancelled) setListings(data || []);
            } catch {
                if (!cancelled) setListings([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [category, subcategory]);

    const grouped = useMemo(() => {
        const m = { all: [...listings] };
        listings.forEach((p) => {
            if (p.collection) (m[p.collection] = m[p.collection] || []).push(p);
        });
        return m;
    }, [listings]);

    const themes = getAllThemes();
    const themesWithItems = themes.filter((t) => (grouped[t.slug] || []).length > 0);

    if (loading) {
        return <section className="container-x py-8 md:py-12"><div className="text-storm-mist text-center py-20">Loading…</div></section>;
    }

    if (listings.length === 0) {
        return (
            <section className="container-x py-8 md:py-12">
                <div className="surface-solid p-12 text-center text-storm-mist">
                    <ImageOff className="w-10 h-10 text-storm-mist/40 mx-auto mb-4" />
                    <p className="text-sm">Nothing here yet.</p>
                </div>
            </section>
        );
    }

    // ---- THEME PICKER (shown first) ----
    if (activeTheme === null) {
        return (
            <section data-testid="theme-picker" className="container-x py-8 md:py-12">
                <p className="eyebrow flex items-center gap-2 mb-5">
                    <BoltIcon className="w-3.5 h-3.5" /> Browse by theme
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
                    {themesWithItems.map((t) => (
                        <ThemeCard
                            key={t.slug}
                            label={t.name}
                            image={t.image}
                            count={(grouped[t.slug] || []).length}
                            onClick={() => setActiveTheme(t.slug)}
                            testid={`theme-card-${t.slug}`}
                        />
                    ))}
                    <ThemeCard
                        label="All Items"
                        count={grouped.all.length}
                        onClick={() => setActiveTheme("all")}
                        testid="theme-card-all"
                        highlight
                    />
                </div>
            </section>
        );
    }

    // ---- PRODUCTS for the chosen theme ----
    const visible = activeTheme === "all" ? grouped.all : (grouped[activeTheme] || []);
    const activeName = activeTheme === "all"
        ? "All Items"
        : (themes.find((t) => t.slug === activeTheme)?.name || activeTheme);

    return (
        <section data-testid="product-listings" className="container-x py-8 md:py-12">
            <div className="flex items-center justify-between gap-4 mb-6">
                <button
                    onClick={() => setActiveTheme(null)}
                    data-testid="back-to-themes"
                    className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-storm-bolt hover:gap-3 transition-all"
                >
                    <ArrowLeft className="w-4 h-4" /> Themes
                </button>
                <h2 className="font-heading uppercase tracking-wide text-white text-lg">{activeName} ({visible.length})</h2>
            </div>

            {visible.length === 0 ? (
                <div className="surface-solid p-12 text-center text-storm-mist">
                    <ImageOff className="w-10 h-10 text-storm-mist/40 mx-auto mb-4" />
                    <p className="text-sm">Nothing in this theme yet.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 md:gap-6">
                    {visible.map((l) => (
                        <ListingCard
                            key={l.listing_slug + (l.subcategory || "")}
                            listing={l}
                            category={category}
                            subcategory={subcategory}
                            productType={productType}
                            defaultPrice={defaultPrice}
                        />
                    ))}
                </div>
            )}
        </section>
    );
};

const ThemeCard = ({ label, count, onClick, testid, highlight, image }) => (
    <button
        onClick={onClick}
        data-testid={testid}
        className={`relative overflow-hidden aspect-[4/3] border transition-colors text-left p-4 flex flex-col justify-end group ${
            highlight
                ? "border-storm-bolt/40 bg-gradient-to-br from-storm-bolt/15 to-storm-void hover:border-storm-bolt"
                : "border-white/10 bg-gradient-to-br from-storm-navy to-storm-void hover:border-storm-bolt/50"
        }`}
    >
        {image && (
            <>
                <img src={image} alt={label} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-storm-void via-storm-void/50 to-transparent" />
            </>
        )}
        <div className="relative">
            <BoltIcon className="w-4 h-4 text-storm-bolt mb-2 opacity-70" />
            <div className="font-heading text-lg md:text-xl uppercase tracking-tight text-white leading-none">{label}</div>
            <div className="text-[11px] text-storm-mist mt-1 flex items-center gap-1">
                {count} item{count === 1 ? "" : "s"} <ArrowRight className="w-3 h-3 text-storm-bolt group-hover:translate-x-1 transition-transform" />
            </div>
        </div>
    </button>
);

const ListingCard = ({ listing, category, subcategory, productType, defaultPrice }) => {
    const title = listing.title || `Custom · ${listing.listing_slug?.slice(0, 8)?.toUpperCase()}`;
    const firstImg = listing.image_ids?.[0];
    const detailHref = `/shop/${category}/${subcategory || listing.subcategory || "all"}/${listing.listing_slug}`;

    const effectivePrice = listing.price_cents || defaultPrice || null;

    const priceLabel = (() => {
        if (productType === PRODUCT_TYPES.SHIRT) return `From $${ADULT_BASE_PRICE} · Kids $${KIDS_BASE_PRICE}`;
        if (effectivePrice) return fmtPrice(effectivePrice);
        return "Quote";
    })();

    return (
        <Link
            to={detailHref}
            data-testid={`listing-card-${listing.listing_slug}`}
            className="surface-solid group flex flex-col hover:border-storm-bolt/40 transition-colors"
        >
            <div className="aspect-square overflow-hidden bg-storm-void relative">
                {firstImg ? (
                    <img
                        src={`${API}/photos/${firstImg}/image`}
                        alt={title}
                        loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                ) : (
                    <div className="absolute inset-0 flex items-center justify-center text-storm-mist/30">
                        <ImageOff className="w-10 h-10" />
                    </div>
                )}
                {listing.collection && (
                    <div className="absolute top-3 left-3 inline-flex items-center gap-1 bg-storm-void/85 backdrop-blur-sm border border-white/10 px-2.5 py-1 text-[10px] uppercase tracking-[0.25em] text-storm-bolt font-heading">
                        {getAllThemes().find((c) => c.slug === listing.collection)?.name || listing.collection}
                    </div>
                )}
                {listing.image_ids?.length > 1 && (
                    <div className="absolute bottom-3 right-3 bg-storm-void/85 backdrop-blur-sm border border-white/10 px-2 py-0.5 text-[10px] uppercase tracking-wider text-white font-heading">
                        +{listing.image_ids.length - 1}
                    </div>
                )}
            </div>
            <div className="p-4 md:p-5 flex flex-col flex-1 gap-2">
                <h3 className="font-heading text-lg text-white leading-tight uppercase line-clamp-2">{title}</h3>
                <p className="text-xs text-storm-mist line-clamp-2 flex-1">
                    {listing.description || "Tap to see full listing & options."}
                </p>
                <div className="flex items-center justify-between pt-2 mt-auto">
                    <span className="font-heading text-storm-bolt">{priceLabel}</span>
                    <span className="text-xs uppercase tracking-[0.25em] text-storm-mist group-hover:text-storm-bolt inline-flex items-center gap-1">
                        View <ArrowRight className="w-3 h-3" />
                    </span>
                </div>
            </div>
        </Link>
    );
};

export default ProductListings;
